import { IconName, Intent, Position } from '@blueprintjs/core';
import { AccessFunctionInterfaceFormData } from 'data/accessFunction/accessFunction.types';
import { DeliveryFunctionNetworkInterface } from 'data/deliveryFunctionNetworkInterface/deliveryFunctionNetworkInterface.types';
import { SipSettingSection } from 'data/sipSetting/sipSetting.types';
import { FieldUpdateHandlerParam } from 'shared/form';

export const TEMPLATE_CATEGORY_ACCESS_FUNCTION = 'access-function';
export const TEMPLATE_CATEGORY_COLLECTION_FUNCTION = 'collection-function';
export const TEMPLATE_CATEGORY_WARRANT_CLASSIC = 'warrants';
export const TEMPLATE_CATEGORY_WARRANT_WIZARD = 'warrants-wizard';
export const TEMPLATE_CATEGORY_CASE_OPTIONS = 'CaseOptions';

export type RawTemplate = {
  category: string
  categoryDefault: boolean
  dateUpdated: string
  id: string
  name: string
  owner: unknown
  publicTemplate: boolean
  template: string
}

export type Template = {
  category: string
  categoryDefault: boolean
  dateUpdated: string
  id: string
  name: string
  owner: unknown
  publicTemplate: boolean
  template: FormTemplate
}

interface FormTemplateSectionMap {
  [key: string]: string | FormTemplateSection
}

export type FormTemplate = FormTemplateSectionMap & {
  version: string,
  name: string,
  key: string, // this is AF tag or CF tag
  formId?: string, // use in the name of dynamic form
  main?: FormTemplateSection,
  facilityInformation?: FormTemplateSection
}

export enum SectionType {
  simpleForm = 'simpleForm',
  distributorConfiguration = 'distributorConfiguration',
  headerSelectionPanel = 'headerSelectionPanel',
}

export type FormTemplateSection = {
  metadata: FormTemplateMetaData
  fields: FormTemplateFieldMap
  general?: FormTemplateSection
}

export type FormTemplateMetaData = {
  sectionType?: SectionType,
  i18nSectionTypeInstructionalTextKey?: string
  sectionTypeInstructionalText?: string
  min?: number
  max?: number
  tabTitle?: string
  layout?: FormTemplateSectionMetadataLayout,
  expandByDefault?: boolean,
  hide?: 'true' | 'false'
  nodeNameField?: keyof AccessFunctionInterfaceFormData,
}

export type FormTemplateSectionMetadataLayout = {
  rows: string[][]
  collapsible?: CollapsibleSection
  additionalSettings?: AdditionalProvisioningSettingsSection
}

export type CollapsibleSection = {
  label: string,
  rows: string[][],
  hide?: boolean
}

export type AdditionalProvisioningSettingsSection = {
  label: string,
  rows: string[][],
  hide?: boolean
}

export type FormTemplateMapByKey = {
  [templateKey: string]: FormTemplate
}

export type FormTemplateFieldMap = {
  [fieldName: string]: FormTemplateField
}

export type ValueToAffectedField = {
  [fieldName: string]: Omit<FormTemplateField, 'type'>
};

export type ValueToAffectedFieldDefinitionMap = {
  [value: string]: {
    layoutOperations?: LayoutOperation[]
    fields: ValueToAffectedField
  }
}

export type LayoutOperation = {
  operation: 'setLayoutOrder'
  parameters: string[]
}

export type FormTemplateValidation = {
  required?: boolean
  validators?: FormTemplateValidator[]
}

export type FormTemplateFieldVisibilityMatch = 'all' | 'any'

export type FormTemplateFieldVisibilityCondition = {
  type: string
  name: string
  operator?: string
  value?: string | number | boolean | string[]
}

export type FormTemplateFieldVisibility = {
  match?: FormTemplateFieldVisibilityMatch
  conditions: FormTemplateFieldVisibilityCondition[]
}

export type FormTemplateVisibilityProperties = {
  [key: string]: string | number | boolean | string[] | undefined
}

export interface FormTemplateField {
  label?: string
  type: string
  options?: FormTemplateOption[]
  optionsFormat?: string
  optionsFormatValue?: string
  optionsTranslatable?: boolean
  optionsFirstTranslatable?: boolean
  allOptions?: FormTemplateOption[]
  optionsMap?: { [key: string]: string[]}
  initial?: string | number | boolean | string[]
  readOnly?: boolean
  disabled?: boolean
  hide?: 'true' | 'false'
  renderAsEmptyIfHidden?: boolean
  className?: string
  readOnlyIfOneOption?: boolean
  timePrecision?: 'minute' | 'second'
  position?: Position
  validation?: FormTemplateValidation,
  placeholder?: string
  intent?: Intent
  minimal?: boolean
  icon?: IconName | string
  handleClick?: () => void
  customComponent?: string
  affectedFieldsOnChange?: string[]
  affectedFieldsOnChangeExtended?:ValueToAffectedFieldDefinitionMap
  processOptionParamName?: string
  processOptionLoadParamValueTo?: 'optionsFormatValue'
  optionsBasedOn?: string
  tabIndex?: number
  filterable?: boolean
  inline?: boolean
  message?: string
  allowCreate?: boolean
  preventAutoSelect?: boolean
  securitylvl?: string
  authpasswd?: string
  privpasswd?: string
  model?: string,
  typeofservice?: string,
  typeofservicemask?: string,
  vpnrouteforward?: string,
  hintMessage?: string,
  nodeNameField?: string,
  preventInitialSelection?: boolean
  priority?: boolean,
  netidNodeTypes?: string[],
  tags?: string[],
  hi1endpointid?: string[]
  visibility?: FormTemplateFieldVisibility
}

export type FormTemplateValidator = {
  type: string
  length?: number
  regexp?: FormTemplateValidatorRegexp
  operator?: string
  fieldName?: string
  value?: string | number
  intervals?: [number, number][]
  customValidationFunction?: string
  invalidIPs?: [string]
  theOtherField?: string
  errorMessage?: string
  i18nErrorMessageKey?: string
  formSectionName?: string
  compareToKey?: string
}

export type FormTemplateValidatorRegexp = string | FormTemplateValidatorRegexEntries[]
export type FormTemplateValidatorRegexEntries = 'letters' | 'digits' | 'space' | 'stars' | 'hexadecimals' | string;

export type FormTemplateOption = {
  id?: string | number
  label: string
  value: string
}

export enum AFFormTemplateFormSectionName {
  provisioningInterfaces = 'provisioningInterfaces',
  dataInterfaces = 'dataInterfaces',
  contentInterfaces = 'contentInterfaces',
  afOptions = 'afOptions',
  monitoringCenters = 'monitoringCenters',
  territories = 'territories',
  eimsNetworkElement = 'eimsNetworkElement',
  diameter = 'diameter',
  manualEntry = 'manualEntry',
  ipAssignment = 'ipAssignment',
  fileImportExport = 'fileImportExport',
  usnbdAgencies = 'usnbdAgencies',
  usnbdCallDataChannels = 'usnbdCallDataChannels',
}

export enum AFFormTemplateFormSubSectionName {
  trafficManager = 'trafficManager',
  distributorConfiguration = 'distributorConfiguration',
}

export const DTLSversions = [
  {
    label: 'DTLSv1',
    value: 'DTLSv1',
  },
  {
    label: 'DTLSv1.2',
    value: 'DTLSv1.2',
  },
];

export const defaultTslOptions = [
  {
    label: 'SSLv3',
    value: 'SSLv3',
  },
  {
    label: 'TLSv1',
    value: 'TLSv1',
  },
  {
    label: 'TLSv1.1',
    value: 'TLSv1.1',
  },
  {
    label: 'TLSv1.2',
    value: 'TLSv1.2',
  },
  {
    label: 'TLSv1.3',
    value: 'TLSv1.3',
  },
];

export enum DistributorsConfigFormTemplateFormSectionName {
  listeners = 'listeners',
  destinations = 'destinations',
}

export type DistributorConfigFieldUpdateCallback = (
  param: FieldUpdateHandlerParam,
  formSectionName: DistributorsConfigFormTemplateFormSectionName,
  tabIndex: number,
) => void;

export type DialedDigitExtractionServerUpdateCallback = (
  param: FieldUpdateHandlerParam,
) => void;

export type TLSProfileUpdateCallback = (
  param: FieldUpdateHandlerParam,
) => void;

export type DeliveryFunctionNetworkInterfaceCallback = (
  param: FieldUpdateHandlerParam,
  formSectionName: DeliveryFunctionNetworkInterface,
  interfaceId?: string | number,
  tabIndex?: number,
) => void;

export type FilterGroupUpdateCallback = (
  param: FieldUpdateHandlerParam,
) => void;

export type SipSettingUpdateCallback = (
  param: FieldUpdateHandlerParam,
  formSectionName: SipSettingSection,
) => void;
